import React, { memo, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import styled from 'styled-components';

import { Button, ButtonSizes, DatePicker, Input } from 'atoms';
import { Colors } from 'context';
import { HomeworksType } from 'types';
import fontService, { FontWeight } from 'services/FontService';
import { desktop, mobile, tablet } from 'services/ui';
import { parseDateFromDatePicker } from 'services/DateService';

export interface ICourseworkCheck {
  students: HomeworksType[];
  lessonDate: Date;
  checkDate: Date;
  isCourseworkChecked: boolean;
  onChangeGrade: (id: string, grade: string) => void;
  onEditCourseworkGrades: () => void;
  onChangeDate: (date: Date | null) => void;
  onSave: () => void;
  onCancel: () => void;
  isEditMode: boolean;
  hasError: boolean;
  addMarksLoading: boolean;
}

export const CourseworkCheckComponent = memo(
  ({
    students,
    lessonDate,
    checkDate,
    isCourseworkChecked,
    onEditCourseworkGrades,
    onChangeDate,
    onChangeGrade,
    onSave,
    onCancel,
    isEditMode,
    hasError,
    addMarksLoading,
  }: ICourseworkCheck) => {
    const { t } = useTranslation();
    const dateString = useMemo(() => parseDateFromDatePicker(checkDate), [checkDate]);

    const inputValidation = useCallback(
      (value: string) => /(^[0-9]+$|^$)/.test(value) && Number(value) <= 100,
      [],
    );
    return (
      <Wrapper>
        <HeaderWrapper>
          <Title>{t('tasks.coursework.checkPercent')}</Title>
          {isCourseworkChecked && (
            <EditButton
              variant="outline"
              size={ButtonSizes.sm}
              colors={{ borderColor: Colors.gray }}
              sidePaddings={12}
              onClick={onEditCourseworkGrades}>
              {t('tasks.coursework.button.edit')}
            </EditButton>
          )}
        </HeaderWrapper>

        <StudentsContainer>
          {students.map(({ studentId, studentName, mark }) => (
            <Student key={studentId}>
              <StudentName>{studentName}</StudentName>
              {isCourseworkChecked ? (
                mark != null && <Text>{mark} %</Text>
              ) : (
                <InputWrapper>
                  <StyledInput
                    placeholder="0"
                    value={mark?.toString() || ''}
                    type="text"
                    onChange={({ target: { value } }) =>
                      inputValidation(value) && onChangeGrade(studentId, value)
                    }
                  />
                  <Text>%</Text>
                </InputWrapper>
              )}
            </Student>
          ))}
        </StudentsContainer>

        <CheckDateWrapper>
          <Title>{t('tasks.coursework.date')}</Title>
          <DateWrapper>
            {isCourseworkChecked ? (
              dateString
            ) : (
              <StyledDatePicker
                value={checkDate}
                onChange={onChangeDate}
                disabledDate={(date) => date < lessonDate}
              />
            )}
          </DateWrapper>
        </CheckDateWrapper>

        {!isCourseworkChecked && (
          <Footer>
            <ButtonsWrapper>
              {hasError && <ErrorText>{t('tasks.coursework.validation')}</ErrorText>}
              <Buttons>
                {isEditMode && (
                  <StyledCancelButton onClick={onCancel} disabled={addMarksLoading} variant="text">
                    {t('tasks.coursework.button.cancel')}
                  </StyledCancelButton>
                )}
                <Button onClick={onSave} disabled={addMarksLoading}>
                  {t('tasks.coursework.button.save')}
                </Button>
              </Buttons>
            </ButtonsWrapper>
          </Footer>
        )}
      </Wrapper>
    );
  },
);

const Wrapper = styled.div`
  ${desktop} {
    width: 700px;
  }
`;

const HeaderWrapper = styled.div`
  margin-bottom: 16px;
  display: flex;
  flex-direction: column;
  gap: 16px;

  ${tablet} {
    margin-bottom: 24px;
    flex-direction: row;
    gap: 12px;
  }
`;

const Title = styled.h3`
  margin: 0;
  ${fontService.getFont({ size: 18, lineHeight: 133, weight: FontWeight.bold })}

  ${tablet} {
    ${fontService.h3()};
  }
`;

const EditButton = styled(Button)`
  width: 100%;
  min-height: 48px;
  ${fontService.text({ size: 16, weight: FontWeight.medium, lineHeight: 150 })};

  ${tablet} {
    min-height: 24px;
    width: max-content;
    ${fontService.text({ size: 12, weight: FontWeight.medium, lineHeight: 133 })};
  }
`;

const StudentsContainer = styled.div`
  width: 100%;
  margin-bottom: 32px;

  ${tablet} {
    width: 458px;
    margin-bottom: 40px;
  }

  ${desktop} {
    margin-bottom: 68px;
  }
`;

const Student = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 0;
  border-top: 1px solid ${Colors.additional_gray};
  &:last-child {
    border-bottom: 1px solid ${Colors.additional_gray};
  }
`;

const StudentName = styled.p`
  ${fontService.text({ size: 14, weight: FontWeight.regular, lineHeight: 142 })}
`;

const InputWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const Text = styled.p`
  ${fontService.text({ size: 14, weight: FontWeight.regular, lineHeight: 142 })}
`;

const StyledInput = styled(Input)`
  input {
    ${fontService.text({ size: 12, lineHeight: 150 })}
    padding: 7px 10px;
    height: 32px;
    width: 50px;
  }
`;

const CheckDateWrapper = styled.div``;

const DateWrapper = styled.div`
  margin-top: 8px;
  ${fontService.text({ size: 16, weight: FontWeight.regular, lineHeight: 150 })};

  ${tablet} {
    margin-top: 24px;
    ${fontService.text({ size: 18, weight: FontWeight.regular, lineHeight: 133 })};
  }
`;

const Footer = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  background-color: ${Colors.bg_gray};
  padding: 20px 20px 32px;

  ${tablet} {
    height: 76px;
    padding: 0 32px;
  }

  ${desktop} {
    padding: 0 0 0 60px;
  }
`;

const ButtonsWrapper = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column-reverse;
  align-items: center;
  row-gap: 16px;

  ${tablet} {
    flex-direction: row;
    justify-content: space-between;
    gap: 0;
  }

  ${desktop} {
    width: 700px;
  }
`;

const ErrorText = styled.p`
  color: ${Colors.signal_red};
  ${fontService.text({ size: 12, lineHeight: 150, weight: FontWeight.regular })}

  ${mobile} {
    text-align: center;
  }
`;

const Buttons = styled.div`
  display: flex;
  flex-direction: column-reverse;
  gap: 8px;
  width: 100%;

  ${tablet} {
    margin-left: auto;
    flex-direction: row;
    gap: 16px;
    width: unset;
  }
`;

const StyledCancelButton = styled(Button)`
  ${mobile} {
    border: 1px solid ${Colors.gray};
    border-radius: 8px;
  }
`;

const StyledDatePicker = styled(DatePicker)`
  margin-bottom: 176px;

  ${tablet} {
    margin-bottom: 80px;
  }

  ${desktop} {
    margin: 0;
  }
`;
